import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useVoiceMessageCount } from '@/hooks/useVoiceMessages';

interface DashboardCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
  color: string;
  enterText: string;
}

interface DashboardCardWithBadgeProps extends DashboardCardProps {
  badge?: number;
}

const DashboardCard: React.FC<DashboardCardWithBadgeProps> = ({ title, description, icon, to, color, enterText, badge }) => {
  return (
    <Link
      to={to}
      className={`block p-4 sm:p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg dark:shadow-gray-700 transition-all duration-200 border-l-4 ${color} relative active:scale-[0.98] touch-manipulation`}
    >
      {/* Pending message badge */}
      {badge !== undefined && badge > 0 && (
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-1 sm:gap-1.5 bg-red-500 text-white text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
          <span>{badge}</span>
          <span className="hidden xs:inline">pending</span>
        </div>
      )}
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-2.5 sm:p-3 rounded-full ${color.replace('border-', 'bg-').replace('-500', '-100')} dark:${color.replace('border-', 'bg-').replace('-500', '-900')}`}>
          {icon}
        </div>
        <div className="ml-3 sm:ml-4 min-w-0 flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">{title}</h3>
          <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{description}</p>
        </div>
      </div>
      <div className="mt-3 sm:mt-4 flex items-center text-sm font-medium text-blue-600 dark:text-blue-400">
        {enterText}
        <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation(['dashboard', 'auth']);
  const { isAuthenticated, isLoading, user } = useAuth();
  const { data: voiceMessageCount } = useVoiceMessageCount();

  // TODO: deprecated — replace with role-based check from JWT when Google OAuth is used in production
  const isAdmin = user?.username === 'derick' || user?.username === 'paul' || user?.username === 'april';

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {isAuthenticated
            ? t('welcomeBack', { name: user?.name })
            : t('welcomeSystem')}
        </h2>
        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
          {isAuthenticated
            ? t('subtitle')
            : t('auth:pleaseLogin')}
        </p>
      </div>

      {/* Dashboard Cards - Only show when authenticated */}
      {isAuthenticated ? (
        <>
          <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {/* Admin-only: SIM Management */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.sim.title')}
                description={t('cards.sim.description')}
                to="/app/sims"
                color="border-blue-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                }
              />
            )}

            {/* Admin-only: Modem Management */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.modem.title')}
                description={t('cards.modem.description')}
                to="/app/modems"
                color="border-green-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                  </svg>
                }
              />
            )}

            {/* All users: Work (Shifts + Teamwork) */}
            <DashboardCard
              title={t('cards.work.title')}
              description={t('cards.work.description')}
              to="/app/work"
              color="border-purple-500"
              enterText={t('enter')}
              icon={
                <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
            />

            {/* Admin-only: PSI Management */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.psi.title')}
                description={t('cards.psi.description')}
                to="/app/psi"
                color="border-indigo-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              />
            )}

            {/* Admin-only: BOM Management */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.bom.title', 'BOM Management')}
                description={t('cards.bom.description', 'Manage Bill of Materials, engineering phases, and ECN workflow')}
                to="/app/bom"
                color="border-teal-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                }
              />
            )}

            {/* Admin-only: MES (Manufacturing Execution System) */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.mes.title', 'MES')}
                description={t('cards.mes.description', 'Manufacturing Execution System - Manage production orders, work orders, and quality control')}
                to="/app/mes"
                color="border-sky-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                }
              />
            )}

            {/* Admin-only: WebFlash MCU Programmer */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.webflash.title', 'WebFlash')}
                description={t('cards.webflash.description', 'Browser-based MCU programmer and debugger via WebUSB')}
                to="/app/webflash"
                color="border-violet-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                }
              />
            )}

            {/* Admin-only: DocFlow (Document Processing Workflows) */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.docflow.title', 'DocFlow')}
                description={t('cards.docflow.description', 'Visual document processing workflows - Extract data from invoices and receipts using AI')}
                to="/app/docflow"
                color="border-teal-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                }
              />
            )}

            {/* All users: Project Management */}
            <DashboardCard
              title={t('cards.project.title', 'Project Management')}
              description={t('cards.project.description', 'Manage product development projects with Gantt, Flow, and Kanban views')}
              to="/app/projects"
              color="border-emerald-500"
              enterText={t('enter')}
              icon={
                <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              }
            />

            {/* Admin-only: Agent Chat Room */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.chat.title', 'Agent Chat Room')}
                description={t('cards.chat.description', 'View and interact with Claude Code sessions on remote machines')}
                to="/app/chat"
                color="border-cyan-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                }
              />
            )}

            {/* Admin-only: LINE Chat Monitor */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.line.title', 'LINE Monitor')}
                description={t('cards.line.description', 'Monitor and interact with LINE Official Account messages')}
                to="/app/line"
                color="border-green-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 5.82 2 10.5c0 4.12 3.53 7.57 8.29 8.35.32.07.76.21.87.48.1.25.07.63.03.88l-.14.84c-.04.25-.2.97.85.53s5.6-3.3 7.64-5.65C21.46 13.64 22 12.13 22 10.5 22 5.82 17.52 2 12 2z" />
                  </svg>
                }
              />
            )}

            {/* Admin-only: Blog Management */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.blog.title', 'Blog Management')}
                description={t('cards.blog.description', 'Manage blog posts and stories for the landing page')}
                to="/app/blog"
                color="border-orange-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                }
              />
            )}

            {/* All users: Pomodoro Timer */}
            <DashboardCard
              title={t('cards.pomodoro.title')}
              description={t('cards.pomodoro.description')}
              to="/app/pomodoro"
              color="border-red-500"
              enterText={t('enter')}
              icon={
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />

            {/* All users: Document Scanner */}
            <DashboardCard
              title={t('cards.camera.title')}
              description={t('cards.camera.description')}
              to="/app/camera"
              color="border-rose-500"
              enterText={t('enter')}
              icon={
                <svg className="w-6 h-6 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
            />

            {/* Admin-only: Barcode Scanner */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.barcode.title', 'Barcode Scanner')}
                description={t('cards.barcode.description', 'Test barcode scanner and print scanned codes')}
                to="/app/barcode-test"
                color="border-amber-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                }
              />
            )}

            {/* Admin-only: Bluetooth Test */}
            {isAdmin && (
              <DashboardCard
                title={t('cards.bluetooth.title', 'Bluetooth')}
                description={t('cards.bluetooth.description', 'Scan and connect to nearby Bluetooth devices')}
                to="/app/bluetooth-test"
                color="border-cyan-500"
                enterText={t('enter')}
                icon={
                  <svg className="w-6 h-6 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                  </svg>
                }
              />
            )}

            {/* All users: Mistake Notebook */}
            <DashboardCard
              title={t('cards.mistakeNotebook.title', 'Mistake Notebook')}
              description={t('cards.mistakeNotebook.description', 'Upload exam papers, frame mistakes, get AI analysis, and generate review practice for kids')}
              to="/app/mistake-notebook"
              color="border-pink-500"
              enterText={t('enter')}
              icon={
                <svg className="w-6 h-6 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              }
            />

            {/* All users: Voice Messaging */}
            <DashboardCard
              title={t('cards.voice.title', 'Voice Messaging')}
              description={t('cards.voice.description', 'Send voice messages to team members. Messages expire after 24 hours.')}
              to="/voice"
              color="border-teal-500"
              enterText={t('enter')}
              badge={voiceMessageCount?.unplayed}
              icon={
                <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              }
            />
          </div>

          {/* Quick Info Section */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/30 dark:to-green-900/30 rounded-lg p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">{t('quickTips.title')}</h3>
            <ul className="space-y-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
              {isAdmin && (
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('quickTips.tips.simManagement')}
                </li>
              )}
              <li className="flex items-start">
                <svg className="w-5 h-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('quickTips.tips.workShifts')}
              </li>
              {isAdmin && (
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('quickTips.tips.psiOperations')}
                </li>
              )}
            </ul>
          </div>
        </>
      ) : (
        /* Login Prompt - Show when not authenticated */
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 p-6 sm:p-8 text-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <svg className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('auth:authRequired')}</h3>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">
            {t('auth:clickToLogin')}
          </p>
          <div className="flex justify-center flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              {t('cards.sim.title')}
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
              {t('cards.modem.title')}
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t('features.workShiftRecord')}
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('cards.psi.title')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
